import type { SystemRolePermissionId } from '@clockwise/shared';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useUnitsWhere } from '@/lib/business-units';

interface Props {
  /** The unit picked, or undefined for every unit. */
  value: string | undefined;
  onChange: (businessUnitId: string | undefined) => void;
  /** The list's view permission: the units offered are those where the
   *  caller sees these records. */
  permission: SystemRolePermissionId;
}

/**
 * Narrow a list to one business unit. Shown only when it can matter — the
 * caller sees this list's records in more than one unit — or when a unit is
 * already picked (a link from a unit's page), so it can be cleared.
 */
export function BusinessUnitFilter({ value, onChange, permission }: Props) {
  const businessUnits = useUnitsWhere(permission);
  if (businessUnits.length < 2 && !value) return null;
  const picked = value && !businessUnits.some((u) => u.id === value);
  return (
    <Select value={value ?? 'all'} onValueChange={(v) => onChange(v === 'all' ? undefined : v)}>
      <SelectTrigger className="w-52" aria-label="Business Unit">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All Business Units</SelectItem>
        {businessUnits.map((u) => (
          <SelectItem key={u.id} value={u.id}>
            {u.name}
          </SelectItem>
        ))}
        {picked ? <SelectItem value={value}>Another Business Unit</SelectItem> : null}
      </SelectContent>
    </Select>
  );
}
